import { StorageFile } from "@/schema"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

// The record types that can link a book's storage integration folder. The value
// doubles as the REST path segment (e.g. `households/:id/storage/...`).
export type StorageRecordType = "households" | "contacts" | "businesses"

export interface StorageBrowseResponse {
  files: StorageFile[]
  parent_folder?: {
    id: string
    name: string
    web_url: string
  }
}

export async function browseStorage(
  context: ApiContext,
  recordType: StorageRecordType,
  recordId: string,
  folderId?: string,
  signal?: AbortSignal
): Promise<StorageBrowseResponse> {
  const params = new URLSearchParams()
  if (folderId) {
    params.append("folder_id", folderId)
  }
  return apiContextFetch<StorageBrowseResponse>(
    context,
    `${recordType}/${recordId}/storage/browse?${params.toString()}`,
    { signal }
  )
}

export async function setStorageFolder(
  context: ApiContext,
  recordType: StorageRecordType,
  recordId: string,
  folderId: string | null
): Promise<void> {
  return apiContextFetch(context, `${recordType}/${recordId}/storage/folder`, {
    method: "PATCH",
    body: JSON.stringify({ folder_id: folderId }),
  })
}

export async function createStorageFolderLink(
  context: ApiContext,
  recordType: StorageRecordType,
  recordId: string,
  folderId: string
): Promise<{ web_url: string }> {
  return apiContextFetch<{ web_url: string }>(context, `${recordType}/${recordId}/storage/folder_links`, {
    method: "POST",
    body: JSON.stringify({ folder_id: folderId }),
  })
}

export async function uploadFileToStorage(
  context: ApiContext,
  recordType: StorageRecordType,
  recordId: string,
  file: File,
  folderId?: string,
  subfolder?: string
): Promise<StorageFile> {
  const formData = new FormData()
  formData.append("file", file)
  if (folderId) {
    formData.append("folder_id", folderId)
  }
  if (subfolder) {
    formData.append("subfolder", subfolder)
  }

  return apiContextFetch<StorageFile>(context, `${recordType}/${recordId}/storage/upload`, {
    method: "POST",
    body: formData,
    headers: {
      "Content-Type": undefined as any, // eslint-disable-line @typescript-eslint/no-explicit-any
    },
  })
}

export async function createFolderInStorage(
  context: ApiContext,
  recordType: StorageRecordType,
  recordId: string,
  folderName: string,
  parentFolderId?: string
): Promise<StorageFile> {
  return apiContextFetch<StorageFile>(context, `${recordType}/${recordId}/storage/create_folder`, {
    method: "POST",
    body: JSON.stringify({
      name: folderName,
      parent_folder_id: parentFolderId,
    }),
  })
}

export async function renameFolderInStorage(
  context: ApiContext,
  recordType: StorageRecordType,
  recordId: string,
  folderId: string,
  newName: string
): Promise<StorageFile> {
  return apiContextFetch<StorageFile>(context, `${recordType}/${recordId}/storage/rename_folder`, {
    method: "PATCH",
    body: JSON.stringify({
      folder_id: folderId,
      name: newName,
    }),
  })
}
